import apiClient from './client'

// Types
export interface Badge {
  id: number
  name: string
  code: string
  slug?: string  // Backend returns slug, mapped to code
  description: string
  icon: string
  color?: string
  type: 'achievement' | 'special' | 'seasonal' | string
  points_value: number
  earned_at?: string
  is_earned?: boolean
}

export interface UserLevel {
  level: number
  current_points: number
  total_points: number
  next_level_points: number
  progress_percentage: number
}

export interface LeaderboardEntry {
  rank: number
  user: {
    id: number
    full_name: string
    avatar: string | null
    role: string
  }
  points: number
  badges_count: number
  level: number
}

export interface PointTransaction {
  id: number
  action: string
  points: number
  description: string
  created_at: string
}

export interface EscapeRoom {
  id: number
  title: string
  slug: string
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  time_limit_minutes: number
  points_reward: number
  badge?: Badge
  thumbnail_url: string | null
  is_completed?: boolean
  best_time_seconds?: number
  attempts_count: number
}

export interface EscapeRoomSession {
  id: number
  escape_room_id: number
  started_at: string
  completed_at: string | null
  current_stage: number
  hints_used: number
  is_completed: boolean
  points_earned: number | null
  time_seconds: number | null
}

export interface EscapeRoomStage {
  id: number
  order: number
  title: string
  description: string
  challenges: EscapeRoomChallenge[]
}

export interface EscapeRoomChallenge {
  id: number
  type: 'question' | 'puzzle' | 'code'
  question: string
  hint?: string
  points_penalty_hint: number
}

export const gamificationApi = {
  // User Stats - combines data from multiple endpoints
  getMyStats: async (): Promise<{
    level: UserLevel
    badges: Badge[]
    recent_points: PointTransaction[]
    rank: number
  }> => {
    // Get points info from my-points endpoint
    const pointsResponse = await apiClient.get('/gamification/my-points/')
    const badgesResponse = await apiClient.get('/gamification/my-badges/')

    // Handle both array and paginated response for badges
    const badgesData = badgesResponse.data
    let badges: Badge[] = []
    if (Array.isArray(badgesData)) {
      badges = badgesData
    } else if (badgesData?.results && Array.isArray(badgesData.results)) {
      badges = badgesData.results
    }

    // Map UserBadge format to Badge format if needed
    badges = badges.map((ub: { badge?: Badge; awarded_at?: string } & Badge) => {
      if (ub.badge) {
        return {
          ...ub.badge,
          code: ub.badge.slug || ub.badge.code,
          earned_at: ub.awarded_at,
          is_earned: true,
        }
      }
      return ub
    })

    return {
      level: pointsResponse.data.level || { level: 1, current_points: 0, total_points: 0, next_level_points: 100, progress_percentage: 0 },
      badges,
      recent_points: pointsResponse.data.recent_transactions || [],
      rank: pointsResponse.data.rank || 0
    }
  },

  getMyBadges: async (): Promise<Badge[]> => {
    const response = await apiClient.get('/gamification/my-badges/')
    // UserBadge returns {badge: {...}, awarded_at, ...}. Extract badge and add earned_at
    const data = Array.isArray(response.data) ? response.data : (response.data.results || [])
    return data.map((ub: { badge: Badge; awarded_at: string }) => ({
      ...ub.badge,
      code: ub.badge.slug,  // Map slug to code for compatibility
      earned_at: ub.awarded_at,
      is_earned: true,
    }))
  },

  getMyPoints: async (params?: {
    page?: number
    limit?: number
  }): Promise<{
    total: number
    transactions: PointTransaction[]
  }> => {
    const response = await apiClient.get('/gamification/my-points/', { params })
    return {
      total: response.data.total_points || 0,
      transactions: response.data.recent_transactions || []
    }
  },

  // Badges
  getAllBadges: async (): Promise<Badge[]> => {
    const response = await apiClient.get('/gamification/badges/')
    // Handle both array and paginated response
    const data = Array.isArray(response.data) ? response.data : (response.data.results || [])
    // Map slug to code for compatibility
    return data.map((badge: Badge & { slug?: string }) => ({
      ...badge,
      code: badge.code || badge.slug || '',
    }))
  },

  getBadge: async (code: string): Promise<Badge & {
    earned_by_count: number
    requirements: string[]
  }> => {
    const response = await apiClient.get(`/gamification/badges/${code}/`)
    return response.data
  },

  // Leaderboard
  getLeaderboard: async (params?: {
    period?: 'weekly' | 'monthly' | 'all_time'
    limit?: number
  }): Promise<LeaderboardEntry[]> => {
    const response = await apiClient.get('/gamification/leaderboard/', { params })
    // Handle {period, rankings, user_rank} response format
    if (response.data.rankings) {
      return response.data.rankings
    }
    if (Array.isArray(response.data)) {
      return response.data
    }
    return []
  },

  getMyRank: async (period?: 'weekly' | 'monthly' | 'all_time'): Promise<{
    rank: number
    total_users: number
    percentile: number
  }> => {
    const response = await apiClient.get('/gamification/leaderboard/my-rank/', {
      params: { period },
    })
    return response.data
  },

  // Escape Rooms
  getEscapeRooms: async (): Promise<EscapeRoom[]> => {
    const response = await apiClient.get('/gamification/escape-rooms/')
    return response.data
  },

  getEscapeRoom: async (slug: string): Promise<EscapeRoom & {
    stages: EscapeRoomStage[]
    leaderboard: {
      user: { id: number; full_name: string }
      time_seconds: number
      completed_at: string
    }[]
  }> => {
    const response = await apiClient.get(`/gamification/escape-rooms/${slug}/`)
    return response.data
  },

  startEscapeRoom: async (escapeRoomId: number): Promise<EscapeRoomSession> => {
    const response = await apiClient.post(`/gamification/escape-rooms/${escapeRoomId}/start/`)
    return response.data
  },

  submitChallengeAnswer: async (
    sessionId: number,
    challengeId: number,
    answer: string
  ): Promise<{
    is_correct: boolean
    next_challenge?: EscapeRoomChallenge
    stage_completed?: boolean
    escape_completed?: boolean
    points_earned?: number
  }> => {
    const response = await apiClient.post(
      `/gamification/escape-sessions/${sessionId}/submit/`,
      { challenge_id: challengeId, answer }
    )
    return response.data
  },

  useHint: async (sessionId: number, challengeId: number): Promise<{
    hint: string
    hints_remaining: number
  }> => {
    const response = await apiClient.post(
      `/gamification/escape-sessions/${sessionId}/hint/`,
      { challenge_id: challengeId }
    )
    return response.data
  },

  getSessionStatus: async (sessionId: number): Promise<EscapeRoomSession> => {
    const response = await apiClient.get(`/gamification/escape-sessions/${sessionId}/`)
    return response.data
  },

  // ============ ADMIN ENDPOINTS ============

  // Admin Stats
  getAdminStats: async (): Promise<GamificationStats> => {
    const response = await apiClient.get('/gamification/admin/stats/')
    return response.data
  },

  // Admin Badges
  getAdminBadges: async (params?: { type?: string; is_active?: boolean }): Promise<AdminBadge[]> => {
    const response = await apiClient.get('/gamification/admin/badges/', { params })
    return response.data.results || response.data
  },

  createBadge: async (data: Partial<AdminBadge>): Promise<AdminBadge> => {
    const response = await apiClient.post('/gamification/admin/badges/', data)
    return response.data
  },

  updateBadge: async (slug: string, data: Partial<AdminBadge>): Promise<AdminBadge> => {
    const response = await apiClient.patch(`/gamification/admin/badges/${slug}/`, data)
    return response.data
  },

  deleteBadge: async (slug: string): Promise<void> => {
    await apiClient.delete(`/gamification/admin/badges/${slug}/`)
  },

  awardBadgeToUser: async (slug: string, userId: number, reason?: string): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/gamification/admin/badges/${slug}/award_to_user/`, {
      user_id: userId,
      reason: reason || 'Assegnato manualmente'
    })
    return response.data
  },

  // Admin User Badges
  getAdminUserBadges: async (params?: { badge?: number; user?: number }): Promise<AdminUserBadge[]> => {
    const response = await apiClient.get('/gamification/admin/user-badges/', { params })
    return response.data.results || response.data
  },

  deleteUserBadge: async (id: number): Promise<void> => {
    await apiClient.delete(`/gamification/admin/user-badges/${id}/`)
  },

  // Admin Point Transactions
  getAdminTransactions: async (params?: { user?: number; action?: string }): Promise<AdminTransaction[]> => {
    const response = await apiClient.get('/gamification/admin/transactions/', { params })
    return response.data.results || response.data
  },

  manualAwardPoints: async (userId: number, points: number, description?: string): Promise<{ detail: string }> => {
    const response = await apiClient.post('/gamification/admin/transactions/manual_award/', {
      user_id: userId,
      points,
      description: description || 'Punti assegnati manualmente'
    })
    return response.data
  },

  // Admin User Levels
  getAdminUserLevels: async (params?: { min_level?: number; search?: string }): Promise<AdminUserLevel[]> => {
    const response = await apiClient.get('/gamification/admin/user-levels/', { params })
    return response.data.results || response.data
  },

  adjustUserPoints: async (id: number, adjustment: number, reason?: string): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/gamification/admin/user-levels/${id}/adjust_points/`, {
      adjustment,
      reason: reason || 'Aggiustamento manuale'
    })
    return response.data
  },

  // Admin Escape Rooms
  getAdminEscapeRooms: async (params?: { difficulty?: number; is_active?: boolean }): Promise<AdminEscapeRoom[]> => {
    const response = await apiClient.get('/gamification/admin/escape-rooms/', { params })
    return response.data.results || response.data
  },

  // Admin Red Flag Hunts
  getAdminRedFlagHunts: async (params?: { is_active?: boolean }): Promise<AdminRedFlagHunt[]> => {
    const response = await apiClient.get('/gamification/admin/red-flag-hunts/', { params })
    return response.data.results || response.data
  },
}

// Admin Types
export interface GamificationStats {
  total_points_awarded: number
  total_badges_awarded: number
  active_users_count: number
  escape_rooms_completed: number
  red_flag_hunts_completed: number
  avg_user_level: number
  top_action: string
  points_by_action: Record<string, { total_points: number; count: number }>
}

export interface AdminBadge extends Badge {
  is_active: boolean
  is_secret: boolean
  auto_award: boolean
  award_criteria: Record<string, unknown> | null
  users_count: number
  created_at: string
  updated_at: string
}

export interface AdminUserBadge {
  id: number
  user: number
  user_name: string
  user_email: string
  badge: number
  badge_name: string
  awarded_at: string
  awarded_reason: string
}

export interface AdminTransaction {
  id: number
  user: number
  user_name: string
  user_email: string
  action: string
  points: number
  description: string
  created_at: string
}

export interface AdminUserLevel {
  id: number
  user: number
  user_name: string
  user_email: string
  level: number
  current_points: number
  total_points: number
  badges_count: number
}

export interface AdminEscapeRoom {
  id: number
  title: string
  slug: string
  description: string
  cover_image: string | null
  difficulty: number
  time_limit_minutes: number
  points_reward: number
  badge: number | null
  badge_name: string | null
  is_active: boolean
  min_role: string
  stages_count: number
  completions_count: number
  created_at: string
  updated_at: string
}

export interface AdminRedFlagHunt {
  id: number
  title: string
  slug: string
  description: string
  document_content: string
  points_per_flag: number
  time_limit_minutes: number | null
  is_active: boolean
  flags_count: number
  completions_count: number
  created_at: string
  updated_at: string
}

// ============ TOURNAMENT TYPES ============

export interface Tournament {
  id: number
  title: string
  slug: string
  description: string
  rules?: string
  cover_image?: string
  registration_start: string
  registration_end: string
  start_date: string
  end_date: string
  time_limit_minutes: number
  max_participants?: number
  participants_count: number
  max_attempts: number
  questions_count: number
  shuffle_questions: boolean
  show_correct_answers: boolean
  show_live_leaderboard: boolean
  status: 'draft' | 'registration' | 'in_progress' | 'completed' | 'archived'
  is_registered: boolean
  is_joinable: boolean
  my_participation?: TournamentParticipant
  points_reward_1st: number
  points_reward_2nd: number
  points_reward_3rd: number
  points_participation: number
  badge_winner?: Badge
  badge_participant?: Badge
}

export interface TournamentParticipant {
  id: number
  user: {
    id: number
    full_name?: string
    display_name?: string
    avatar?: string
    avatar_url?: string | null
  }
  registered_at: string
  status: 'registered' | 'started' | 'completed' | 'disqualified'
  best_score?: number | null
  best_time_seconds?: number | null
  final_rank?: number | null
  points_awarded: number
  attempts_count: number
}

export interface TournamentQuestion {
  id: number
  text: string
  type: 'single' | 'multiple' | 'true_false'
  points: number
  order: number
  answers: TournamentAnswer[]
}

export interface TournamentAnswer {
  id: number
  text: string
}

export interface TournamentAttempt {
  id: number
  attempt_number: number
  started_at: string
  completed_at?: string
  score: number
  max_score: number
  correct_answers: number
  total_questions: number
  time_taken_seconds?: number
  is_completed: boolean
}

export interface TournamentLeaderboardEntry {
  rank: number
  user_id: number
  display_name: string
  avatar?: string
  best_score: number
  best_time_seconds?: number
  attempts_count: number
}

export interface TournamentQuizStart {
  attempt_id: number
  questions: TournamentQuestion[]
  time_limit_seconds: number
  started_at: string
}

export interface TournamentQuizResult {
  score: number
  max_score: number
  correct_answers: number
  total_questions: number
  time_taken_seconds: number
  rank?: number
  points_earned: number
}

// ============ TOURNAMENT API ============

export const tournamentApi = {
  // List tournaments
  getTournaments: async (params?: { status?: string }): Promise<Tournament[]> => {
    const response = await apiClient.get('/gamification/tournaments/', { params })
    return response.data.results || response.data
  },

  // Get tournament detail
  getTournament: async (slug: string): Promise<Tournament> => {
    const response = await apiClient.get(`/gamification/tournaments/${slug}/`)
    return response.data
  },

  // Register for tournament
  register: async (slug: string): Promise<{ detail: string; participant: TournamentParticipant }> => {
    const response = await apiClient.post(`/gamification/tournaments/${slug}/register/`)
    return response.data
  },

  // Start tournament quiz
  startQuiz: async (slug: string): Promise<TournamentQuizStart> => {
    const response = await apiClient.post(`/gamification/tournaments/${slug}/start/`)
    return response.data
  },

  // Submit quiz answers
  submitQuiz: async (
    attemptId: number,
    answers: Record<string, number[]>
  ): Promise<TournamentQuizResult> => {
    const response = await apiClient.post(`/gamification/tournament-attempts/${attemptId}/submit/`, {
      answers
    })
    return response.data
  },

  // Get tournament leaderboard
  getLeaderboard: async (slug: string): Promise<{
    tournament: string
    rankings: TournamentLeaderboardEntry[]
    user_rank?: number
    total_participants: number
  }> => {
    const response = await apiClient.get(`/gamification/tournaments/${slug}/leaderboard/`)
    return response.data
  },

  // Get user's tournaments
  getMyTournaments: async (): Promise<TournamentParticipant[]> => {
    const response = await apiClient.get('/gamification/tournaments/my/')
    return response.data.results || response.data
  },

  // Admin: Get all tournaments
  getAdminTournaments: async (params?: { status?: string; is_active?: boolean }): Promise<AdminTournament[]> => {
    const response = await apiClient.get('/gamification/admin/tournaments/', { params })
    return response.data.results || response.data
  },

  // Admin: Create tournament
  createTournament: async (data: AdminTournamentInput): Promise<AdminTournament> => {
    const response = await apiClient.post('/gamification/admin/tournaments/', data)
    return response.data
  },

  // Admin: Update tournament
  updateTournament: async (slug: string, data: Partial<AdminTournamentInput>): Promise<AdminTournament> => {
    const response = await apiClient.patch(`/gamification/admin/tournaments/${slug}/`, data)
    return response.data
  },

  // Admin: Delete tournament
  deleteTournament: async (slug: string): Promise<void> => {
    await apiClient.delete(`/gamification/admin/tournaments/${slug}/`)
  },

  // Admin: Get tournament participants
  getParticipants: async (slug: string): Promise<TournamentParticipant[]> => {
    const response = await apiClient.get(`/gamification/admin/tournaments/${slug}/participants/`)
    return response.data
  },

  // Admin: Get tournament questions
  getQuestions: async (slug: string): Promise<AdminTournamentQuestion[]> => {
    const response = await apiClient.get(`/gamification/admin/tournaments/${slug}/questions/`)
    return response.data
  },

  // Admin: Add question to tournament
  addQuestion: async (
    slug: string,
    data: {
      text: string
      type?: string
      points?: number
      explanation?: string
      answers: { text: string; is_correct: boolean }[]
    }
  ): Promise<AdminTournamentQuestion> => {
    const response = await apiClient.post(`/gamification/admin/tournaments/${slug}/add_question/`, data)
    return response.data
  },

  // Admin: Update question in tournament
  updateQuestion: async (
    slug: string,
    questionId: number,
    data: {
      text?: string
      type?: string
      points?: number
      explanation?: string
      answers?: { text: string; is_correct: boolean }[]
    }
  ): Promise<AdminTournamentQuestion> => {
    const response = await apiClient.patch(`/gamification/admin/tournaments/${slug}/questions/${questionId}/`, data)
    return response.data
  },

  // Admin: Delete question from tournament
  deleteQuestion: async (slug: string, questionId: number): Promise<void> => {
    await apiClient.delete(`/gamification/admin/tournaments/${slug}/questions/${questionId}/delete/`)
  },

  // Admin: Finalize tournament
  finalizeTournament: async (slug: string): Promise<{
    detail: string
    winners: TournamentParticipant[]
  }> => {
    const response = await apiClient.post(`/gamification/admin/tournaments/${slug}/finalize/`)
    return response.data
  },
}

export interface AdminTournament extends Tournament {
  questions_created: number
  badge_winner_name?: string
  badge_participant_name?: string
  is_active: boolean
  min_role: string
  created_at: string
  updated_at: string
}

// Input type for creating/updating tournaments (uses badge IDs instead of objects)
export interface AdminTournamentInput {
  title: string
  description: string
  rules?: string
  registration_start: string
  registration_end: string
  start_date: string
  end_date: string
  time_limit_minutes: number
  max_participants?: number
  max_attempts: number
  questions_count: number
  shuffle_questions: boolean
  show_correct_answers: boolean
  show_live_leaderboard: boolean
  points_reward_1st: number
  points_reward_2nd: number
  points_reward_3rd: number
  points_participation: number
  badge_winner?: number  // Badge ID
  badge_participant?: number  // Badge ID
  status: 'draft' | 'registration' | 'in_progress' | 'completed' | 'archived'
  is_active: boolean
  min_role: string
}

export interface AdminTournamentQuestion {
  id: number
  text: string
  type: string
  points: number
  order: number
  explanation?: string
  answers: { id: number; text: string; is_correct: boolean }[]
}
